import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { naturalizationQuestions, questionsByCategory, getRandomQuestions, searchQuestions, specialConsiderationQuestions, getQuestionsWithMultipleChoice, speakText, stopSpeaking, isSpeaking } from '../data/naturalizationQuestions';

const NaturalizationTest = () => {
  const [mode, setMode] = useState('intro'); // intro, audio-study, flashcards, quiz, results
  const [currentQuestions, setCurrentQuestions] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [showAnswer, setShowAnswer] = useState(false);
  const [userEmail, setUserEmail] = useState('');
  const [userZip, setUserZip] = useState('');
  const [quizAnswers, setQuizAnswers] = useState([]);
  const [quizScore, setQuizScore] = useState(0);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [quizSettings, setQuizSettings] = useState({
    questionCount: 20, // Official test format
    showCorrectAnswer: true,
    randomOrder: true,
    audioEnabled: true
  });
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentUtterance, setCurrentUtterance] = useState(null);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [autoAdvance, setAutoAdvance] = useState(true);
  const [autoAdvanceTimer, setAutoAdvanceTimer] = useState(null);
  const searchInputRef = useRef(null);
  const navigate = useNavigate();

  // Memoize search results to prevent unnecessary re-renders
  const searchResults = useMemo(() => {
    if (!searchTerm.trim()) return naturalizationQuestions;
    return searchQuestions(searchTerm);
  }, [searchTerm]);

  // Stable search input handler with preventDefault
  const handleSearchChange = useCallback((e) => {
    e.preventDefault();
    setSearchTerm(e.target.value);
  }, []);

  // Prevent form submission
  const handleSearchSubmit = useCallback((e) => {
    e.preventDefault();
    return false;
  }, []);

  // Memoize categories to prevent unnecessary re-renders
  const categories = useMemo(() => [
    { id: 'all', name: 'All Questions', count: naturalizationQuestions.length },
    { id: 'Principles of American Government', name: 'Principles of American Government', count: questionsByCategory['Principles of American Government']?.length || 0 },
    { id: 'System of Government', name: 'System of Government', count: questionsByCategory['System of Government']?.length || 0 },
    { id: 'Rights and Responsibilities', name: 'Rights and Responsibilities', count: questionsByCategory['Rights and Responsibilities']?.length || 0 },
    { id: 'Colonial Period and Independence', name: 'Colonial Period and Independence', count: questionsByCategory['Colonial Period and Independence']?.length || 0 },
    { id: '1800s', name: '1800s', count: questionsByCategory['1800s']?.length || 0 },
    { id: 'Recent American History', name: 'Recent American History', count: questionsByCategory['Recent American History']?.length || 0 },
    { id: 'Symbols', name: 'Symbols', count: questionsByCategory['Symbols']?.length || 0 },
    { id: 'Holidays', name: 'Holidays', count: questionsByCategory['Holidays']?.length || 0 },
    { id: 'special', name: '65/20 Special (20 questions)', count: specialConsiderationQuestions?.length || 0 }
  ], []);

  // Auto-advance functionality for audio mode
  const startAutoAdvance = () => {
    if (mode === 'audio-study' && autoAdvance) {
      const timer = setTimeout(() => {
        if (currentIndex < currentQuestions.length - 1) {
          setCurrentIndex(currentIndex + 1);
          setShowAnswer(false);
          setIsPlaying(false);
        } else {
          setMode('intro');
        }
      }, 2000); // 2 seconds to advance to next question
      
      setAutoAdvanceTimer(timer);
    }
  };

  // Clear auto-advance timer when needed
  const clearAutoAdvance = () => {
    if (autoAdvanceTimer) {
      clearTimeout(autoAdvanceTimer);
      setAutoAdvanceTimer(null);
    }
  };

  // Audio functions with auto-advance support
  const handlePlayAudio = (text) => {
    clearAutoAdvance();
    
    if (isPlaying) {
      stopSpeaking();
      setIsPlaying(false);
      setCurrentUtterance(null);
    } else {
      const utterance = speakText(text);
      if (utterance) {
        setIsPlaying(true);
        setCurrentUtterance(utterance);
        
        utterance.onend = () => {
          setIsPlaying(false);
          setCurrentUtterance(null);
          
          // In audio mode, auto-play the answer after question, then auto-advance
          if (mode === 'audio-study' && text === currentQuestions[currentIndex]?.question && !showAnswer) {
            setTimeout(() => {
              setShowAnswer(true);
              setTimeout(() => {
                const answerUtterance = speakText(currentQuestions[currentIndex]?.answer);
                if (answerUtterance) {
                  setIsPlaying(true);
                  setCurrentUtterance(answerUtterance);
                  answerUtterance.onend = () => {
                    setIsPlaying(false);
                    setCurrentUtterance(null);
                    startAutoAdvance();
                  };
                }
              }, 1000);
            }, 1000);
          } else if (mode === 'audio-study' && autoAdvance) {
            startAutoAdvance();
          }
        };
        
        utterance.onerror = () => {
          setIsPlaying(false);
          setCurrentUtterance(null);
        };
      }
    }
  };

  // Auto-play question when entering audio study mode or advancing
  useEffect(() => {
    if (mode === 'audio-study' && currentQuestions.length > 0 && autoAdvance) {
      setTimeout(() => {
        const question = currentQuestions[currentIndex];
        if (question) {
          handlePlayAudio(question.question);
        }
      }, 1000);
    }
    
    return () => {
      clearAutoAdvance();
    };
  }, [mode, currentIndex]);

  // Stop audio when component unmounts or mode changes
  useEffect(() => {
    return () => {
      stopSpeaking();
      clearAutoAdvance();
    };
  }, [mode]);

  const startAudioStudy = (category = 'all') => {
    stopSpeaking();
    setIsPlaying(false);
    
    let questions = category === 'all' 
      ? naturalizationQuestions 
      : category === 'special'
        ? specialConsiderationQuestions
        : questionsByCategory[category] || [];
    
    if (searchTerm) {
      questions = searchQuestions(searchTerm);
    }
    
    if (quizSettings.randomOrder) {
      questions = [...questions].sort(() => 0.5 - Math.random());
    }
    
    setCurrentQuestions(questions);
    setCurrentIndex(0);
    setShowAnswer(false);
    setMode('audio-study');
  };

  const startFlashcards = (category = 'all') => {
    stopSpeaking();
    setIsPlaying(false);
    
    let questions = category === 'all' 
      ? naturalizationQuestions 
      : category === 'special'
        ? specialConsiderationQuestions
        : questionsByCategory[category] || [];
    
    if (searchTerm) {
      questions = searchQuestions(searchTerm);
    }
    
    if (quizSettings.randomOrder) {
      questions = [...questions].sort(() => 0.5 - Math.random());
    }
    
    setCurrentQuestions(questions);
    setCurrentIndex(0);
    setShowAnswer(false);
    setMode('flashcards');
  };

  const startQuiz = (category = 'all') => {
    stopSpeaking();
    setIsPlaying(false);
    
    let questions = category === 'all' 
      ? naturalizationQuestions 
      : category === 'special'
        ? specialConsiderationQuestions
        : questionsByCategory[category] || [];
    
    // Apply search filter if search term exists
    if (searchTerm) {
      questions = searchQuestions(searchTerm);
    }
    
    if (quizSettings.randomOrder) {
      questions = [...questions].sort(() => 0.5 - Math.random());
    }
    
    questions = questions.slice(0, quizSettings.questionCount);
    
    // Add multiple choice options to quiz questions
    const questionsWithMC = getQuestionsWithMultipleChoice(questions);
    
    setCurrentQuestions(questionsWithMC);
    setCurrentIndex(0);
    setQuizAnswers([]);
    setSelectedAnswer(null);
    setMode('quiz');
  };

  const nextCard = () => {
    if (currentIndex < currentQuestions.length - 1) {
      setCurrentIndex(currentIndex + 1);
      setShowAnswer(false);
    } else {
      setMode('intro');
    }
  };

  const prevCard = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
      setShowAnswer(false);
    }
  };

  const submitQuizAnswer = (selectedOptionIndex) => {
    const question = currentQuestions[currentIndex];
    const isCorrect = selectedOptionIndex === question.multipleChoice.correctIndex;
    
    const newAnswers = [...quizAnswers, { 
      questionId: question.id, 
      selectedOption: selectedOptionIndex,
      correctOption: question.multipleChoice.correctIndex,
      isCorrect 
    }];
    
    setQuizAnswers(newAnswers);
    
    if (currentIndex < currentQuestions.length - 1) {
      setCurrentIndex(currentIndex + 1);
      setSelectedAnswer(null);
    } else {
      // Calculate final score
      const correct = newAnswers.filter(a => a.isCorrect).length;
      setQuizScore(correct);
      setMode('results');
    }
  };

  const IntroScreen = () => (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-gradient-to-r from-navy-700 to-blue-700 border-b border-blue-800 sticky top-0 z-40">
        <div className="container mx-auto px-4 py-3 flex items-center justify-between">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-1.5 text-slate-200 hover:text-white hover:bg-white/10 px-3 py-2 rounded-lg transition-colors text-sm font-medium"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18" />
            </svg>
            Back to Home
          </button>
          <span className="text-sm font-semibold text-white">Citizenship Test Prep</span>
          <div className="w-24" />
        </div>
      </header>
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="mb-4" aria-hidden="true"><svg className="w-12 h-12 text-navy-700 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M12 21a9.004 9.004 0 008.716-6.747M12 21a9.004 9.004 0 01-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3m0 18c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3m0 0a8.997 8.997 0 017.843 4.582M12 3a8.997 8.997 0 00-7.843 4.582m15.686 0A11.953 11.953 0 0112 10.5c-2.998 0-5.74-1.1-7.843-2.918m15.686 0A8.959 8.959 0 0121 12c0 .778-.099 1.533-.284 2.253m0 0A17.919 17.919 0 0112 16.5c-3.162 0-6.133-.815-8.716-2.247m0 0A9.015 9.015 0 013 12c0-1.605.42-3.113 1.157-4.418" /></svg></div>
          <h1 className="text-4xl font-bold text-navy-700 mb-4">
            U.S. Naturalization Study App (2025 Version)
          </h1>
          <p className="text-xl text-slate-600 font-medium max-w-2xl mx-auto mb-4">
            Master the 128 civics questions for your naturalization test with flashcards, quizzes, and comprehensive review.
          </p>
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 max-w-2xl mx-auto">
            <p className="text-sm text-amber-700 font-semibold">
              <span aria-hidden="true">📅</span> <strong>New 2025 Version:</strong> Updated with 128 official questions (effective October 20, 2025).
              Test format: Answer 20 questions, pass with 12+ correct answers.
            </p>
          </div>
        </div>

        {/* Optional Information */}
        <div className="max-w-md mx-auto bg-white rounded-xl p-6 border border-slate-200 mb-8">
          <div className="space-y-4">
            <div>
              <label htmlFor="user-zip" className="block text-gray-700 text-sm font-bold mb-2">
                Zip Code (Optional)
              </label>
              <input
                id="user-zip"
                type="text"
                value={userZip}
                onChange={(e) => setUserZip(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
                placeholder="Enter your zip code"
              />
              <p className="text-xs text-gray-500 mt-1">
                This helps us provide accurate answers for questions about your senators, representatives, and governor.
              </p>
            </div>

            <div>
              <label htmlFor="user-email" className="block text-gray-700 text-sm font-bold mb-2">
                Email (Optional)
              </label>
              <input
                id="user-email"
                type="email"
                value={userEmail}
                onChange={(e) => setUserEmail(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
                placeholder="Enter your email"
              />
              <p className="text-xs text-gray-500 mt-1">
                We'll send you helpful study materials and test updates.
              </p>
            </div>
          </div>
        </div>

        {/* Study Options */}
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl font-bold text-navy-700 mb-6 text-center">Choose Your Study Method</h2>
          
          {/* Category Selection */}
          <div className="bg-white rounded-xl p-6 border border-slate-200 mb-6">
            <h3 className="text-lg font-bold text-gray-800 mb-4"><span aria-hidden="true">📚</span> Select Category</h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {categories.map((category) => (
                <button
                  key={category.id}
                  onClick={() => setSelectedCategory(category.id)}
                  className={`p-3 rounded-lg border transition-all text-sm font-semibold ${
                    selectedCategory === category.id
                      ? 'border-blue-600 bg-blue-50 text-blue-700'
                      : 'border-slate-200 hover:border-blue-300 text-slate-700'
                  }`}
                >
                  <div>{category.name}</div>
                  <div className="text-xs text-gray-600">{category.count} questions</div>
                </button>
              ))}
            </div>
          </div>

          {/* Search */}
          <div className="bg-white rounded-xl p-6 border border-slate-200 mb-6">
            <h3 className="text-lg font-bold text-gray-800 mb-4"><span aria-hidden="true">🔍</span> Search Questions</h3>
            <form onSubmit={handleSearchSubmit}>
              <input
                ref={searchInputRef}
                id="question-search"
                type="text"
                aria-label="Search by question content, answer, or category"
                value={searchTerm}
                onChange={handleSearchChange}
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
                placeholder="Search by question content, answer, or category (e.g., 'president', 'constitution', 'war')..."
                autoComplete="off"
                autoCorrect="off"
                spellCheck="false"
              />
            </form>
            {searchTerm && (
              <div className="mt-2 text-sm text-gray-600">
                {searchResults.length} questions found for "{searchTerm}"
              </div>
            )}
          </div>

          {/* Study Methods */}
          <div className="grid md:grid-cols-3 gap-6 mb-8">
            {/* Audio Study */}
            <div className="bg-white rounded-xl p-6 border border-slate-200 flex flex-col">
              <div className="text-4xl mb-4 text-center" aria-hidden="true">🎧</div>
              <h3 className="text-xl font-bold text-navy-700 mb-3 text-center">Audio Study</h3>
              <p className="text-gray-600 mb-5 text-center">
                Listen to questions and answers. Perfect for studying while driving or walking.
              </p>
              <div className="text-sm text-blue-600 mb-6 font-medium space-y-1 flex-1">
                <div><span aria-hidden="true">✓</span> Hands-free learning</div>
                <div><span aria-hidden="true">✓</span> Perfect for commuting</div>
                <div><span aria-hidden="true">✓</span> Auto-play questions & answers</div>
              </div>
              <button
                onClick={() => startAudioStudy(selectedCategory)}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 px-4 rounded-lg transition-colors font-semibold mt-auto"
              >
                <span aria-hidden="true">🎧</span> Start Audio Study
              </button>
            </div>

            {/* Flashcards */}
            <div className="bg-white rounded-xl p-6 border border-slate-200 flex flex-col">
              <div className="text-4xl mb-4 text-center" aria-hidden="true">📋</div>
              <h3 className="text-xl font-bold text-navy-700 mb-3 text-center">Flashcard Study</h3>
              <p className="text-gray-600 mb-5 text-center">
                Study questions one by one. Click to reveal answers and build your knowledge.
              </p>
              <div className="text-sm text-blue-600 mb-6 font-medium space-y-1 flex-1">
                <div><span aria-hidden="true">✓</span> Self-paced learning</div>
                <div><span aria-hidden="true">✓</span> Visual memorization</div>
                <div><span aria-hidden="true">✓</span> Optional audio support</div>
              </div>
              <button
                onClick={() => startFlashcards(selectedCategory)}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 px-4 rounded-lg transition-colors font-semibold mt-auto"
              >
                <span aria-hidden="true">📚</span> Start Flashcards
              </button>
            </div>

            {/* Practice Quiz */}
            <div className="bg-white rounded-xl p-6 border border-slate-200 flex flex-col">
              <div className="text-4xl mb-4 text-center" aria-hidden="true">🎯</div>
              <h3 className="text-xl font-bold text-navy-700 mb-3 text-center">Practice Quiz</h3>
              <p className="text-gray-600 mb-5 text-center">
                Take a timed quiz with multiple choice questions. Test your knowledge like the real exam.
              </p>
              <div className="text-sm text-blue-600 mb-5 font-medium space-y-1">
                <div><span aria-hidden="true">✓</span> Multiple choice format</div>
                <div><span aria-hidden="true">✓</span> Pass/fail grading (12/20)</div>
                <div><span aria-hidden="true">✓</span> Official test simulation</div>
              </div>

              {/* Quiz Settings */}
              <div className="mb-5 flex-1">
                <div className="flex items-center justify-between">
                  <label htmlFor="quiz-question-count" className="text-sm font-medium text-gray-700">Questions:</label>
                  <select
                    id="quiz-question-count"
                    value={quizSettings.questionCount}
                    onChange={(e) => setQuizSettings({...quizSettings, questionCount: parseInt(e.target.value)})}
                    className="border rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                  >
                    <option value={5}>5</option>
                    <option value={10}>10</option>
                    <option value={20}>20 (Official Test)</option>
                    <option value={50}>50</option>
                  </select>
                </div>
              </div>

              <button
                onClick={() => startQuiz(selectedCategory)}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 px-4 rounded-lg transition-colors font-semibold mt-auto"
              >
                <span aria-hidden="true">🎯</span> Start Practice Quiz
              </button>
            </div>
          </div>

          {/* Quick Stats */}
          <div className="bg-white rounded-xl p-6 border border-slate-200 text-center">
            <h3 className="text-lg font-bold text-gray-800 mb-4"><span aria-hidden="true">📊</span> Study Statistics</h3>
            <div className="grid grid-cols-3 gap-4 text-sm">
              <div>
                <div className="text-2xl font-bold text-blue-600">{naturalizationQuestions.length}</div>
                <div className="text-gray-600">Total Questions (2025)</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-navy-700">8</div>
                <div className="text-gray-600">Categories</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-emerald-600" aria-hidden="true">🔊</div>
                <div className="text-gray-600">Audio Support</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const AudioStudyScreen = () => {
    const question = currentQuestions[currentIndex];
    if (!question) return <IntroScreen />;

    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="container mx-auto px-4 py-8">
          <div className="max-w-2xl mx-auto">
            {/* Header */}
            <div className="text-center mb-6">
              <button
                onClick={() => setMode('intro')}
                className="mb-4 text-slate-600 hover:text-navy-700 font-semibold"
              >
                ← Back to Menu
              </button>
              <div className="text-2xl mb-2" aria-hidden="true">🎧</div>
              <h2 className="text-xl font-bold text-navy-700">
                Audio Study {currentIndex + 1} of {currentQuestions.length}
              </h2>
              <div className="text-sm text-gray-600 mt-1">{question.category}</div>
            </div>

            {/* Progress Bar */}
            <div className="w-full bg-gray-200 rounded-full h-2 mb-6">
              <div
                className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${((currentIndex + 1) / currentQuestions.length) * 100}%` }}
              ></div>
            </div>

            {/* Audio Study Card */}
            <div className="bg-white rounded-xl p-8 border border-slate-200 mb-6">
              <div className="text-center">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center space-x-2">
                    {question.specialConsideration && (
                      <span className="bg-amber-50 text-amber-700 px-2 py-1 rounded text-xs font-semibold">
                        <span aria-hidden="true">⭐</span> 65/20 Special
                      </span>
                    )}
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className="text-emerald-600 font-semibold"><span aria-hidden="true">🎧</span> Hands-Free Mode</span>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={autoAdvance}
                        onChange={(e) => setAutoAdvance(e.target.checked)}
                        className="mr-2"
                      />
                      <span className="text-sm">Auto-advance</span>
                    </label>
                  </div>
                </div>
                
                <div className="text-lg font-semibold text-gray-800 mb-6">
                  {question.question}
                </div>
                
                <div className="grid grid-cols-2 gap-4 mb-6">
                  <button
                    onClick={() => handlePlayAudio(question.question)}
                    className={`p-4 rounded-lg transition-colors ${
                      isPlaying ? 'bg-red-500 text-white' : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                    }`}
                  >
                    <span aria-hidden="true">{isPlaying ? '⏹️' : '🔊'}</span> {isPlaying ? 'Stop' : 'Play'} Question
                  </button>

                  <button
                    onClick={() => handlePlayAudio(question.answer)}
                    className="bg-blue-100 text-blue-700 hover:bg-blue-200 p-4 rounded-lg transition-colors"
                  >
                    <span aria-hidden="true">🗣️</span> Play Answer
                  </button>
                </div>
                
                {showAnswer && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                    <div className="text-blue-800 font-semibold">
                      {question.answer}
                    </div>
                  </div>
                )}

                <button
                  onClick={() => setShowAnswer(!showAnswer)}
                  className="bg-blue-600 hover:bg-blue-700 text-white py-3 px-6 rounded-lg transition-colors font-semibold mb-4"
                >
                  {showAnswer ? 'Hide Answer' : 'Show Answer'}
                </button>
              </div>
            </div>

            {/* Navigation */}
            <div className="flex justify-between">
              <button
                onClick={prevCard}
                disabled={currentIndex === 0}
                className="border border-slate-200 text-slate-700 py-2 px-4 rounded-lg hover:bg-slate-50 transition-colors disabled:opacity-40 font-semibold"
              >
                ← Previous
              </button>
              
              <button
                onClick={nextCard}
                className="bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-lg transition-colors font-semibold"
              >
                {currentIndex === currentQuestions.length - 1 ? 'Finish' : 'Next →'}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const FlashcardScreen = () => {
    const question = currentQuestions[currentIndex];
    if (!question) return <IntroScreen />;

    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="container mx-auto px-4 py-8">
          <div className="max-w-2xl mx-auto">
            {/* Header */}
            <div className="text-center mb-6">
              <button
                onClick={() => setMode('intro')}
                className="mb-4 text-slate-600 hover:text-navy-700 font-semibold"
              >
                ← Back to Menu
              </button>
              <div className="mb-2" aria-hidden="true"><svg className="w-8 h-8 text-navy-700 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M12 21a9.004 9.004 0 008.716-6.747M12 21a9.004 9.004 0 01-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3m0 18c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3m0 0a8.997 8.997 0 017.843 4.582M12 3a8.997 8.997 0 00-7.843 4.582m15.686 0A11.953 11.953 0 0112 10.5c-2.998 0-5.74-1.1-7.843-2.918m15.686 0A8.959 8.959 0 0121 12c0 .778-.099 1.533-.284 2.253m0 0A17.919 17.919 0 0112 16.5c-3.162 0-6.133-.815-8.716-2.247m0 0A9.015 9.015 0 013 12c0-1.605.42-3.113 1.157-4.418" /></svg></div>
              <h2 className="text-xl font-bold text-navy-700">
                Flashcard {currentIndex + 1} of {currentQuestions.length}
              </h2>
              <div className="text-sm text-gray-600 mt-1">{question.category}</div>
            </div>

            {/* Progress Bar */}
            <div className="w-full bg-gray-200 rounded-full h-2 mb-6">
              <div
                className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${((currentIndex + 1) / currentQuestions.length) * 100}%` }}
              ></div>
            </div>

            {/* Flashcard */}
            <div className="bg-white rounded-xl p-8 border border-slate-200 mb-6">
              <div className="text-center">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center space-x-2">
                    {question.specialConsideration && (
                      <span className="bg-amber-50 text-amber-700 px-2 py-1 rounded text-xs font-semibold">
                        <span aria-hidden="true">⭐</span> 65/20 Special
                      </span>
                    )}
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => handlePlayAudio(question.question)}
                      className={`p-2 rounded-full transition-colors ${
                        isPlaying ? 'bg-red-500 text-white' : 'bg-blue-100 text-blue-600 hover:bg-blue-200'
                      }`}
                      title="Play/Stop Audio"
                      aria-label={isPlaying ? 'Stop question audio' : 'Play question audio'}
                    >
                      <span aria-hidden="true">{isPlaying ? '⏹️' : '🔊'}</span>
                    </button>
                  </div>
                </div>

                <div className="text-lg font-semibold text-gray-800 mb-6">
                  {question.question}
                </div>

                {!showAnswer ? (
                  <button
                    onClick={() => setShowAnswer(true)}
                    className="bg-blue-600 hover:bg-blue-700 text-white py-3 px-6 rounded-lg transition-colors font-semibold"
                  >
                    Show Answer
                  </button>
                ) : (
                  <div>
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                      <div className="text-blue-800 font-semibold">
                        {question.answer}
                      </div>
                    </div>
                    {quizSettings.audioEnabled && (
                      <button
                        onClick={() => handlePlayAudio(question.answer)}
                        className={`p-2 rounded-full transition-colors ${
                          isPlaying ? 'bg-red-500 text-white' : 'bg-blue-100 text-blue-600 hover:bg-blue-200'
                        }`}
                        title="Play Answer Audio"
                      >
                        <span aria-hidden="true">{isPlaying ? '⏹️' : '🔊'}</span> Answer
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Navigation */}
            <div className="flex justify-between">
              <button
                onClick={prevCard}
                disabled={currentIndex === 0}
                className="border border-slate-200 text-slate-700 py-2 px-4 rounded-lg hover:bg-slate-50 transition-colors disabled:opacity-40 font-semibold"
              >
                ← Previous
              </button>
              
              <button
                onClick={nextCard}
                className="bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-lg transition-colors font-semibold"
              >
                {currentIndex === currentQuestions.length - 1 ? 'Finish' : 'Next →'}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const QuizScreen = () => {
    const question = currentQuestions[currentIndex];
    
    if (!question) return <IntroScreen />;

    const options = question.multipleChoice?.options || [];
    const optionLabels = ['A', 'B', 'C', 'D'];

    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="container mx-auto px-4 py-8">
          <div className="max-w-2xl mx-auto">
            {/* Header */}
            <div className="text-center mb-6">
              <button
                onClick={() => setMode('intro')}
                className="mb-4 text-slate-600 hover:text-navy-700 font-semibold"
              >
                ← Back to Menu
              </button>
              <div className="text-2xl mb-2" aria-hidden="true">🎯</div>
              <h2 className="text-xl font-bold text-navy-700">
                Question {currentIndex + 1} of {currentQuestions.length}
              </h2>
              <div className="text-sm text-gray-600 mt-1">{question.category}</div>
            </div>

            {/* Progress Bar */}
            <div className="w-full bg-gray-200 rounded-full h-2 mb-6">
              <div
                className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${((currentIndex + 1) / currentQuestions.length) * 100}%` }}
              ></div>
            </div>

            {/* Quiz Question */}
            <div className="bg-white rounded-xl p-8 border border-slate-200 mb-6">
              <div className="mb-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center space-x-2">
                    {question.specialConsideration && (
                      <span className="bg-amber-50 text-amber-700 px-2 py-1 rounded text-xs font-semibold">
                        <span aria-hidden="true">⭐</span> 65/20 Special
                      </span>
                    )}
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => handlePlayAudio(question.question)}
                      className={`p-2 rounded-full transition-colors ${
                        isPlaying ? 'bg-red-500 text-white' : 'bg-blue-100 text-blue-600 hover:bg-blue-200'
                      }`}
                      title="Play/Stop Audio"
                      aria-label={isPlaying ? 'Stop question audio' : 'Play question audio'}
                    >
                      <span aria-hidden="true">{isPlaying ? '⏹️' : '🔊'}</span>
                    </button>
                  </div>
                </div>
                
                <div className="text-lg font-semibold text-gray-800 mb-6">
                  {question.question}
                </div>
                
                {/* Multiple Choice Options */}
                <div className="space-y-3 mb-6" role="radiogroup" aria-label={question.question}>
                  {options.map((option, index) => (
                    <button
                      key={index}
                      role="radio"
                      aria-checked={selectedAnswer === index}
                      onClick={() => setSelectedAnswer(index)}
                      className={`w-full p-4 text-left rounded-lg border transition-all duration-200 ${
                        selectedAnswer === index
                          ? 'border-blue-500 bg-blue-50 text-blue-800'
                          : 'border-gray-300 hover:border-blue-300 hover:bg-blue-50'
                      }`}
                    >
                      <span className="font-bold text-blue-600 mr-3">{optionLabels[index]}.</span>
                      {option}
                    </button>
                  ))}
                </div>
              </div>
              
              <button
                onClick={() => {
                  if (selectedAnswer !== null) {
                    submitQuizAnswer(selectedAnswer);
                  }
                }}
                disabled={selectedAnswer === null}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 px-4 rounded-lg transition-colors disabled:opacity-50 font-semibold"
              >
                {selectedAnswer !== null ? `Submit Answer (${optionLabels[selectedAnswer]})` : 'Select an Answer'}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const ResultsScreen = () => {
    const totalQuestions = currentQuestions.length;
    const percentage = Math.round((quizScore / totalQuestions) * 100);
    
    // Official USCIS test: need 12 out of 20 correct (60%)
    // For other quiz lengths, maintain 60% passing rate
    const passingScore = Math.ceil(totalQuestions * 0.6);
    const passed = quizScore >= passingScore;
    
    // Special message for official 20-question test
    const isOfficialTest = totalQuestions === 20;

    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="container mx-auto px-4 py-8">
          <div className="max-w-2xl mx-auto">
            <div className="bg-white rounded-xl p-8 border border-slate-200 text-center">
              <div className="text-6xl mb-4" aria-hidden="true">
                {passed ? '🎉' : '📚'}
              </div>
              
              <h2 className="text-2xl font-bold text-navy-700 mb-4">
                {isOfficialTest ? 'Official Practice Test Complete!' : 'Quiz Complete!'}
              </h2>
              
              <div className="text-4xl font-bold mb-4">
                <span className={passed ? 'text-green-600' : 'text-red-600'}>
                  {quizScore}/{totalQuestions}
                </span>
                <span className="text-gray-500"> ({percentage}%)</span>
              </div>
              
              <div className={`text-xl font-bold mb-4 p-3 rounded-lg ${
                passed
                  ? 'bg-blue-50 text-blue-700 border border-blue-200'
                  : 'bg-red-50 text-red-700 border border-red-200'
              }`}>
                <span aria-hidden="true">{passed ? '✅' : '❌'}</span> {passed ? 'PASS' : 'FAIL'}
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                <div className="text-sm text-blue-800 font-semibold mb-2">
                  <span aria-hidden="true">📊</span> Grading Information:
                </div>
                <div className="text-sm text-blue-700">
                  {isOfficialTest 
                    ? `You need 12+ correct answers out of 20 to pass the official USCIS naturalization test.`
                    : `You need ${passingScore}+ correct answers out of ${totalQuestions} to pass (60% passing rate).`
                  }
                </div>
              </div>
              
              <p className="text-gray-600 mb-6">
                {passed 
                  ? isOfficialTest
                    ? 'Congratulations! You\'re ready for the actual USCIS naturalization test. Keep studying to maintain your knowledge.'
                    : 'Great job! You passed this practice quiz. Try the official 20-question test when you\'re ready.'
                  : isOfficialTest
                    ? 'Keep studying! You need at least 12 correct answers to pass the actual test. Review the questions you missed and try again.'
                    : 'Keep studying and try again. Focus on the areas where you need improvement.'
                }
              </p>
              
              {/* Show detailed results for official test */}
              {isOfficialTest && (
                <div className="bg-gray-50 border rounded-lg p-4 mb-6 text-left">
                  <h4 className="font-bold text-gray-800 mb-3 text-center"><span aria-hidden="true">📋</span> Answer Review</h4>
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {quizAnswers.map((answer, index) => (
                      <div key={index} className={`text-xs p-2 rounded ${
                        answer.isCorrect ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}>
                        <span className="font-semibold">
                          Q{index + 1}: <span aria-hidden="true">{answer.isCorrect ? '✓' : '✗'}</span>
                          <span className="sr-only">{answer.isCorrect ? 'Correct' : 'Incorrect'}</span>
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              <div className="space-y-3">
                <button
                  onClick={() => setMode('intro')}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 px-4 rounded-lg transition-colors font-semibold"
                >
                  <span aria-hidden="true">📚</span> Back to Study Menu
                </button>

                <button
                  onClick={() => startQuiz(selectedCategory)}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 px-4 rounded-lg transition-colors font-semibold"
                >
                  <span aria-hidden="true">🔄</span> Retake Quiz
                </button>

                {!isOfficialTest && (
                  <button
                    onClick={() => {
                      setQuizSettings({...quizSettings, questionCount: 20});
                      startQuiz(selectedCategory);
                    }}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 px-4 rounded-lg transition-colors font-semibold"
                  >
                    <span aria-hidden="true">🎯</span> Take Official 20-Question Test
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  switch (mode) {
    case 'audio-study':
      return <AudioStudyScreen />;
    case 'flashcards':
      return <FlashcardScreen />;
    case 'quiz':
      return <QuizScreen />;
    case 'results':
      return <ResultsScreen />;
    default:
      return <IntroScreen />;
  }
};

export default NaturalizationTest;